import React, { useState, useEffect } from 'react';
import axios from 'axios';

function ObjectiveManagement({ discipline = 'stem' }) {
  const [gradebands, setGradebands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedGrades, setExpandedGrades] = useState({});
  const [expandedOutcomes, setExpandedOutcomes] = useState({});

  // Modal states
  const [showGradebandModal, setShowGradebandModal] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [showObjectiveModal, setShowObjectiveModal] = useState(false);
  
  const [editingGradeband, setEditingGradeband] = useState(null);
  const [editingOutcome, setEditingOutcome] = useState(null);
  const [editingObjective, setEditingObjective] = useState(null);
  const [selectedGradebandId, setSelectedGradebandId] = useState(null);
  const [selectedOutcomeId, setSelectedOutcomeId] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [selectedGradeband, setSelectedGradeband] = useState(null);

  useEffect(() => {
    loadObjectives();
  }, [discipline]);

  const loadObjectives = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/outcomes/api/objectives-hierarchy', { 
        params: { discipline },
        withCredentials: true
      });
      setGradebands(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load objectives');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleGradebandExpand = (id) => {
    setExpandedGrades(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleOutcomeExpand = (id) => {
    setExpandedOutcomes(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleAddGradeband = () => {
    setEditingGradeband(null);
    setShowGradebandModal(true);
  };

  const handleEditGradeband = (gradeband) => {
    setEditingGradeband(gradeband);
    setShowGradebandModal(true);
  };

  const handleDeleteGradeband = async (id) => {
    if (!window.confirm('Delete this grade band and all its outcomes/objectives? Evidence will be orphaned.')) {
      return;
    }
    try {
      await axios.delete(`/outcomes/api/gradebands/${id}`);
      loadObjectives();
    } catch (err) {
      setError('Failed to delete grade band');
    }
  };

  const handleSaveGradeband = async (data) => {
    try {
      const payload = { ...data, discipline };
      if (editingGradeband) {
        await axios.put(`/outcomes/api/gradebands/${editingGradeband.id}`, payload);
      } else {
        await axios.post('/outcomes/api/gradebands', payload);
      }
      loadObjectives();
      setShowGradebandModal(false);
    } catch (err) {
      setError('Failed to save grade band');
    }
  };

  const handleAddOutcome = (gradebandId) => {
    // Find the gradeband object to pass its outcomes
    const gb = gradebands.find(g => g.id === gradebandId);
    setSelectedGradeband(gb);
    setSelectedGradebandId(gradebandId);
    setEditingOutcome(null);
    setShowOutcomeModal(true);
  };

  const handleEditOutcome = (outcome, gradebandId) => {
    const gb = gradebands.find(g => g.id === gradebandId);
    setSelectedGradeband(gb);
    setSelectedGradebandId(gradebandId);
    setEditingOutcome(outcome);
    setShowOutcomeModal(true);
  };

  const handleDeleteOutcome = async (id) => {
    if (!window.confirm('Delete this outcome and all its objectives? Evidence will be orphaned.')) {
      return;
    }
    try {
      await axios.delete(`/outcomes/api/outcomes/${id}`);
      loadObjectives();
    } catch (err) {
      setError('Failed to delete outcome');
    }
  };

  const handleSaveOutcome = async (data) => {
    try {
      const { _swap, ...cleanData } = data;
      const payload = { ...cleanData, gradeband_id: selectedGradebandId, discipline };
      
      console.log('[handleSaveOutcome] _swap:', _swap, 'editingOutcome:', editingOutcome?.id, 'payload:', payload);
      
      if (_swap && editingOutcome) {
        // Find the outcome currently at the new position to swap
        const adjacentOut = selectedGradeband?.outcomes?.find(out => out.number === payload.number);
        console.log('[handleSaveOutcome] adjacentOut:', adjacentOut);
        
        if (adjacentOut) {
          // Use a temp number (999999) to avoid unique constraint violation during swap
          // Step 1: Move adjacent outcome to temporary number
          const tempPayload = {
            number: 999999,
            title: adjacentOut.title,
            description: adjacentOut.description,
            gradeband_id: selectedGradebandId,
            discipline
          };
          console.log('[Swap] Step 1: Move adjacent outcome', adjacentOut.id, 'to temp position 999999');
          await axios.put(`/outcomes/api/outcomes/${adjacentOut.id}`, tempPayload);
          
          // Step 2: Move current outcome to the target number
          console.log('[Swap] Step 2: Move current outcome', editingOutcome.id, 'to number', payload.number);
          await axios.put(`/outcomes/api/outcomes/${editingOutcome.id}`, payload);
          
          // Step 3: Move adjacent outcome from temp to old position
          const finalPayload = {
            number: editingOutcome.number,
            title: adjacentOut.title,
            description: adjacentOut.description,
            gradeband_id: selectedGradebandId,
            discipline
          };
          console.log('[Swap] Step 3: Move adjacent outcome', adjacentOut.id, 'to old position', editingOutcome.number);
          await axios.put(`/outcomes/api/outcomes/${adjacentOut.id}`, finalPayload);
        } else {
          console.log('[Save] No adjacent outcome found at number', payload.number);
          await axios.put(`/outcomes/api/outcomes/${editingOutcome.id}`, payload);
        }
      } else if (editingOutcome) {
        await axios.put(`/outcomes/api/outcomes/${editingOutcome.id}`, payload);
      } else {
        await axios.post('/outcomes/api/outcomes', payload);
      }
      loadObjectives();
      // Only close modal if it's not a swap operation
      if (!_swap) {
        setShowOutcomeModal(false);
      }
    } catch (err) {
      setError('Failed to save outcome: ' + err.message);
      console.error('[Error saving outcome]', err);
    }
  };

  const handleAddObjective = (outcomeId, outcome) => {
    setSelectedOutcomeId(outcomeId);
    setSelectedOutcome(outcome);
    setEditingObjective(null);
    setShowObjectiveModal(true);
  };

  const handleEditObjective = (objective, outcomeId, outcome) => {
    setSelectedOutcomeId(outcomeId);
    setSelectedOutcome(outcome);
    setEditingObjective(objective);
    setShowObjectiveModal(true);
  };

  const handleDeleteObjective = async (id) => {
    if (!window.confirm('Delete this objective? Evidence will be orphaned.')) {
      return;
    }
    try {
      await axios.delete(`/outcomes/api/objectives/${id}`);
      loadObjectives();
    } catch (err) {
      setError('Failed to delete objective');
    }
  };

  const handleSaveObjective = async (data) => {
    try {
      const { _swap, ...cleanData } = data;
      const payload = { ...cleanData, outcome_id: selectedOutcomeId, discipline };
      
      console.log('[handleSaveObjective] _swap:', _swap, 'editingObjective:', editingObjective?.id, 'payload:', payload);
      
      if (_swap && editingObjective) {
        // Find the objective currently at the new position to swap
        const adjacentObj = selectedOutcome?.objectives?.find(obj => obj.number === payload.number);
        console.log('[handleSaveObjective] adjacentObj:', adjacentObj);
        
        if (adjacentObj) {
          // Use a temp number (999999) to avoid unique constraint violation during swap
          // Step 1: Move adjacent objective to temporary number
          const tempPayload = {
            number: 999999,
            text: adjacentObj.text,
            outcome_id: selectedOutcomeId,
            discipline
          };
          console.log('[Swap] Step 1: Move adjacent objective', adjacentObj.id, 'to temp position 999999');
          await axios.put(`/outcomes/api/objectives/${adjacentObj.id}`, tempPayload);
          
          // Step 2: Move current objective to the target number
          console.log('[Swap] Step 2: Move current objective', editingObjective.id, 'to number', payload.number);
          await axios.put(`/outcomes/api/objectives/${editingObjective.id}`, payload);
          
          // Step 3: Move adjacent objective from temp to old position
          const finalPayload = {
            number: editingObjective.number,
            text: adjacentObj.text,
            outcome_id: selectedOutcomeId,
            discipline
          };
          console.log('[Swap] Step 3: Move adjacent objective', adjacentObj.id, 'to old position', editingObjective.number);
          await axios.put(`/outcomes/api/objectives/${adjacentObj.id}`, finalPayload);
        } else {
          console.log('[Save] No adjacent objective found at number', payload.number);
          await axios.put(`/outcomes/api/objectives/${editingObjective.id}`, payload);
        }
      } else if (editingObjective) {
        await axios.put(`/outcomes/api/objectives/${editingObjective.id}`, payload);
      } else {
        await axios.post('/outcomes/api/objectives', payload);
      }
      loadObjectives();
      setShowObjectiveModal(false);
    } catch (err) {
      setError('Failed to save objective: ' + err.message);
      console.error('[Error saving objective]', err);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get('/outcomes/api/export/objectives', {
        params: { discipline },
        withCredentials: true
      });
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `objectives-${discipline}-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export objectives');
    }
  };

  if (loading) {
    return <div className="loading">Loading objectives...</div>;
  }

  return (
    <div className="objective-management">
      <div className="objective-management-header">
        <h3>Manage Standards &amp; Objectives</h3>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleExport}>
            📥 Export JSON
          </button>
          <button className="btn-primary" onClick={handleAddGradeband}>
            + Add Grade Band
          </button>
        </div>
      </div>

      {error && <div className="error-alert">{error}</div>}

      <div className="gradebands-list">
        {gradebands.map(gradeband => (
          <div key={gradeband.id} className="gradeband-section">
            <div className="gradeband-header">
              <button
                className="expand-toggle"
                onClick={() => toggleGradebandExpand(gradeband.id)}
              >
                {expandedGrades[gradeband.id] ? '▼' : '▶'}
              </button>
              <div className="gradeband-info">
                <h4>{gradeband.title}</h4>
                <p className="text-muted">{gradeband.name}</p>
              </div>
              <div className="gradeband-actions">
                <button
                  className="btn-icon edit"
                  onClick={() => handleEditGradeband(gradeband)}
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  className="btn-icon delete"
                  onClick={() => handleDeleteGradeband(gradeband.id)}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>

            {expandedGrades[gradeband.id] && (
              <div className="gradeband-content">
                <div className="outcomes-list">
                  {gradeband.outcomes && gradeband.outcomes.length > 0 ? (
                    gradeband.outcomes.map(outcome => (
                      <div key={outcome.id} className="outcome-item">
                        <div className="outcome-header">
                          <button
                            className="expand-toggle"
                            onClick={() => toggleOutcomeExpand(outcome.id)}
                          >
                            {expandedOutcomes[outcome.id] ? '▼' : '▶'}
                          </button>
                          <div className="outcome-info">
                            <span className="outcome-number">Outcome {outcome.number}</span>
                            <h5>{outcome.title}</h5>
                          </div>
                          <div className="outcome-actions">
                            <button
                              className="btn-icon edit"
                              onClick={() => handleEditOutcome(outcome, gradeband.id)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-icon delete"
                              onClick={() => handleDeleteOutcome(outcome.id)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>

                        {expandedOutcomes[outcome.id] && (
                          <div className="outcome-content">
                            <div className="objectives-list">
                              {outcome.objectives && outcome.objectives.length > 0 ? (
                                outcome.objectives.map(objective => (
                                  <div key={objective.id} className="objective-item">
                                    <div className="objective-text">
                                      <span className="objective-number">{objective.number + 1}.</span>
                                      <span>{objective.text}</span>
                                    </div>
                                    <div className="objective-actions">
                                      <button
                                        className="btn-icon edit"
                                        onClick={() => handleEditObjective(objective, outcome.id, outcome)}
                                        title="Edit"
                                      >
                                        ✏️
                                      </button>
                                      <button
                                        className="btn-icon delete"
                                        onClick={() => handleDeleteObjective(objective.id)}
                                        title="Delete"
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-muted">No objectives</p>
                              )}
                            </div>
                            <button
                              className="btn-small"
                              onClick={() => handleAddObjective(outcome.id, outcome)}
                            >
                              + Add Objective
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No outcomes</p>
                  )}
                </div>
                <button
                  className="btn-small"
                  onClick={() => handleAddOutcome(gradeband.id)}
                >
                  + Add Outcome
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {showGradebandModal && (
        <GradebandModal
          gradeband={editingGradeband}
          onSave={handleSaveGradeband}
          onClose={() => setShowGradebandModal(false)}
        />
      )}

      {showOutcomeModal && (
        <OutcomeModal
          outcome={editingOutcome}
          gradeband={selectedGradeband}
          onSave={handleSaveOutcome}
          onClose={() => setShowOutcomeModal(false)}
        />
      )}

      {showObjectiveModal && (
        <ObjectiveModal
          objective={editingObjective}
          outcome={selectedOutcome}
          onSave={handleSaveObjective}
          onClose={() => setShowObjectiveModal(false)}
        />
      )}
    </div>
  );
}

// Gradeband Modal Component
function GradebandModal({ gradeband, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: gradeband?.name || '',
    title: gradeband?.title || '',
    description: gradeband?.description || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{gradeband ? 'Edit Grade Band' : 'New Grade Band'}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Name (e.g., grade912) *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="grade912"
              required
            />
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              placeholder="Grades 9-12"
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              rows={3}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="submit-btn">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Outcome Modal Component
function OutcomeModal({ outcome, gradeband, onSave, onClose }) {
  // Calculate the next available number by finding the max existing number
  const calculateNextNumber = () => {
    if (outcome) {
      return outcome.number; // Editing, keep the same number
    }
    if (!gradeband?.outcomes || gradeband.outcomes.length === 0) {
      return 1; // First outcome
    }
    const numbers = gradeband.outcomes.map(out => out.number).filter(n => typeof n === 'number');
    if (numbers.length === 0) return 1;
    return Math.max(...numbers) + 1; // Next number after max
  };

  const getMaxNumber = () => {
    if (!gradeband?.outcomes || gradeband.outcomes.length === 0) {
      return 100;
    }
    const numbers = gradeband.outcomes.map(out => out.number).filter(n => typeof n === 'number');
    if (numbers.length === 0) return 100;
    return Math.max(...numbers) + 2; // Allow one higher than current max
  };

  const [formData, setFormData] = useState({
    number: calculateNextNumber(),
    title: outcome?.title || '',
    description: outcome?.description || ''
  });

  const handleMoveUp = () => {
    if (formData.number > 1) {
      const newNumber = formData.number - 1;
      const data = { ...formData, number: newNumber, _swap: true };
      onSave(data);
    }
  };

  const handleMoveDown = () => {
    const maxNum = getMaxNumber();
    if (formData.number < maxNum - 1) {
      const newNumber = formData.number + 1;
      const data = { ...formData, number: newNumber, _swap: true };
      onSave(data);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{outcome ? 'Edit Outcome' : 'New Outcome'}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Number *</label>
            <div className="number-input-group">
              <button
                type="button"
                className="number-btn"
                onClick={handleMoveUp}
                disabled={formData.number <= 1}
                title="Move up"
              >
                ▲
              </button>
              <input
                type="number"
                value={formData.number}
                onChange={e => setFormData({...formData, number: parseInt(e.target.value) || 1})}
                min="1"
                max={getMaxNumber()}
                required
              />
              <button
                type="button"
                className="number-btn"
                onClick={handleMoveDown}
                disabled={formData.number >= getMaxNumber() - 1}
                title="Move down"
              >
                ▼
              </button>
            </div>
            <p className="form-hint">Use ▲▼ to reorder outcomes</p>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              placeholder="Outcome title"
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              rows={3}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="submit-btn">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Objective Modal Component
function ObjectiveModal({ objective, outcome, onSave, onClose }) {
  // Calculate the next available number by finding the max existing number
  const calculateNextNumber = () => {
    if (objective) {
      return objective.number; // Editing, keep the same number
    }
    if (!outcome?.objectives || outcome.objectives.length === 0) {
      return 0; // First objective
    }
    const numbers = outcome.objectives.map(obj => obj.number).filter(n => typeof n === 'number');
    if (numbers.length === 0) return 0;
    return Math.max(...numbers) + 1; // Next number after max
  };

  const getMaxNumber = () => {
    if (!outcome?.objectives || outcome.objectives.length === 0) {
      return 100;
    }
    const numbers = outcome.objectives.map(obj => obj.number).filter(n => typeof n === 'number');
    if (numbers.length === 0) return 100;
    return Math.max(...numbers) + 2; // Allow one higher than current max
  };

  const [formData, setFormData] = useState({
    number: calculateNextNumber(),
    text: objective?.text || ''
  });

  const handleMoveUp = () => {
    if (formData.number > 0) {
      const newNumber = formData.number - 1;
      const data = { ...formData, number: newNumber, _swap: true };
      onSave(data);
    }
  };

  const handleMoveDown = () => {
    const maxNum = getMaxNumber();
    if (formData.number < maxNum) {
      const newNumber = formData.number + 1;
      const data = { ...formData, number: newNumber, _swap: true };
      onSave(data);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{objective ? 'Edit Objective' : 'New Objective'}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Objective Number</label>
            <div className="number-input-group">
              <button
                type="button"
                className="number-btn"
                onClick={handleMoveUp}
                disabled={formData.number <= 0}
                title="Move up"
              >
                ▲
              </button>
              <input
                type="number"
                value={formData.number}
                onChange={e => setFormData({...formData, number: parseInt(e.target.value) || 0})}
                min="0"
                max={getMaxNumber()}
                required
              />
              <button
                type="button"
                className="number-btn"
                onClick={handleMoveDown}
                disabled={formData.number >= getMaxNumber() - 1}
                title="Move down"
              >
                ▼
              </button>
            </div>
            <p className="form-hint">Use ▲▼ to reorder objectives within this outcome</p>
          </div>
          <div className="form-group">
            <label>Objective Text *</label>
            <textarea
              value={formData.text}
              onChange={e => setFormData({...formData, text: e.target.value})}
              placeholder="Enter objective text"
              rows={4}
              required
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="submit-btn">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ObjectiveManagement;
